// src/components/training/TrainingForm.jsx

import React, { useState, useMemo, useRef, memo } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Divider,
  Menu,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  DragIndicator as DragIndicatorIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
} from '@mui/icons-material';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { v4 as uuidv4 } from 'uuid';

// Configuración de opciones de entrenamiento
const trainingOptions = {
  Carrera: {
    fields: ['tipo', 'titulo'],
    subOptions: {
      tipo: [
        'Calentamiento',
        'Extensivo',
        'Intensivo',
        'Variable',
        'Recuperación',
        'Técnica',
        'Enfriamiento',
        'Otro',
      ],
    },
  },
  Gimnasio: { fields: ['series', 'repeticiones', 'peso'] },
  Ciclismo: { fields: ['duración', 'intensidad', 'velocidad_promedio'] },
  Natación: { fields: ['distancia', 'duración', 'estilo'] },
  Estiramiento: { fields: ['duración', 'zonas_musculares'] },
  Descanso: { fields: [] },
  Evento: { fields: ['lugar', 'hora_de_inicio'] },
  Otro: { fields: ['detalles_adicionales'] },
};

const intensidadOptions = [
  'Ritmo',
  'FC Promedio',
  'Zona FC',
  'Zona de Potencia',
  'Potencia',
  'Intensidad',
  'Elevación',
  'Cadencia',
];

const duraciónOptions = ['Tiempo', 'Distancia'];

const unidadOptions = ['m', 'km'];

// Función para convertir segundos a formato hh:mm:ss
const secondsToHHMMSS = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

// Función para formatear el tiempo según las reglas especificadas
const formatTime = (value) => {
  let digits = value.replace(/\D/g, "");

  // Eliminar ceros a la izquierda
  digits = digits.replace(/^0+/, "");

  // Si todos los caracteres eran ceros, mantener un solo cero
  if (digits.length === 0) digits = "0";

  switch (digits.length) {
    case 1:
    case 2:
      // Interpretar como minutos y segundos
      return `0:${digits.padStart(2, '0')}`;
    case 3:
      // 3 dígitos: 3:25
      return `${digits[0]}:${digits.slice(1).padStart(2, '0')}`;
    case 4:
      // 4 dígitos: 25:00
      return `${digits.slice(0, 2)}:${digits.slice(2).padStart(2, '0')}`;
    case 5:
      // 5 dígitos: 1:25:00
      return `${digits[0]}:${digits.slice(1, 3)}:${digits.slice(3).padStart(2, '0')}`;
    case 6:
      // 6 dígitos: 12:34:56
      return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4).padStart(2, '0')}`;
    default:
      return value; // Retornar el valor original si no coincide
  }
};

// Componentes reutilizables para campos de formulario
const FormSelect = ({ name, label, control, rules, options, error, size = "small" }) => (
  <FormControl fullWidth margin="normal" error={!!error}>
    <InputLabel>{label}</InputLabel>
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <Select {...field} label={label} size={size}>
          {options.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      )}
    />
    {error && (
      <Typography variant="caption" color="error">
        {error.message}
      </Typography>
    )}
  </FormControl>
);

const FormTextField = ({ name, label, control, rules, type = "text", ...props }) => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    render={({ field }) => (
      <TextField
        {...field}
        label={label}
        type={type}
        fullWidth
        margin="normal"
        size="small"
        {...props}
      />
    )}
  />
);

// Subcomponente para manejar cada Parte del Entrenamiento
const PartForm = memo(({
  control,
  index,
  field,
  remove,
  handleMenuAction,
  watch,
  errors,
  dragHandleProps,
  setValue,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  // Función para manejar el menú de opciones
  const handleMenuOpenLocal = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuCloseLocal = () => {
    setAnchorEl(null);
  };

  const handleMenuActionLocal = (action) => {
    handleMenuAction(action, index);
    handleMenuCloseLocal();
  };

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={
          <div onClick={(e) => e.stopPropagation()}>
            <ExpandMoreIcon />
          </div>
        }
        aria-controls={`panel${index}-content`}
        id={`panel${index}-header`}
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <Box {...dragHandleProps} sx={{ mr: 2, cursor: 'grab' }} aria-label="Arrastrar Parte">
          <DragIndicatorIcon />
        </Box>
        <Typography sx={{ flexGrow: 1 }}>Parte {index + 1}</Typography>
        <Tooltip title="Más opciones">
          <IconButton
            onClick={(e) => {
              e.stopPropagation(); // Evita que el clic en el botón abra el Accordion
              handleMenuOpenLocal(e);
            }}
            size="small"
            aria-label="Más opciones de la parte"
          >
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuCloseLocal}
        >
          <MenuItem onClick={() => handleMenuActionLocal('add')}>Añadir Parte</MenuItem>
          <MenuItem onClick={() => handleMenuActionLocal('duplicate')}>Duplicar Parte</MenuItem>
          <MenuItem onClick={() => handleMenuActionLocal('repeat')}>Repetir Parte</MenuItem>
          <MenuItem onClick={() => handleMenuActionLocal('delete')}>Borrar Parte</MenuItem>
        </Menu>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Tipo de la Parte */}
          <Grid item xs={12} sm={6}>
            <FormSelect
              name={`parts.${index}.tipo`}
              label="Tipo de Parte"
              control={control}
              rules={{ required: 'Este campo es requerido' }}
              options={trainingOptions.Carrera.subOptions.tipo}
              error={errors?.parts?.[index]?.tipo}
            />
          </Grid>

          {/* Título de la Parte */}
          <Grid item xs={12} sm={6}>
            <FormTextField
              name={`parts.${index}.titulo`}
              label="Título de la Parte"
              control={control}
              rules={{ required: 'Este campo es requerido' }}
            />
            {errors?.parts?.[index]?.titulo && (
              <Typography variant="caption" color="error">
                {errors.parts[index].titulo.message}
              </Typography>
            )}
          </Grid>

          {/* Duración de la Parte */}
          <Grid item xs={12} sm={6}>
            <FormSelect
              name={`parts.${index}.duración`}
              label="Duración"
              control={control}
              rules={{ required: 'Este campo es requerido' }}
              options={duraciónOptions}
              error={errors?.parts?.[index]?.duración}
            />

            {/* Condicional para Tiempo */}
            {watch(`parts.${index}.duración`) === 'Tiempo' && (
              <FormTextField
                name={`parts.${index}.tiempo`}
                label="Tiempo (hh:mm:ss)"
                control={control}
                rules={{ required: 'Este campo es requerido' }}
                placeholder="00:00:00"
                onChange={(e) => {
                  const formatted = formatTime(e.target.value);
                  setValue(`parts.${index}.tiempo`, formatted);
                }}
                value={watch(`parts.${index}.tiempo`) || ''}
              />
            )}

            {/* Condicional para Distancia */}
            {watch(`parts.${index}.duración`) === 'Distancia' && (
              <Box display="flex" alignItems="center" gap={2} mt={2}>
                <FormTextField
                  name={`parts.${index}.distancia`}
                  label="Distancia"
                  control={control}
                  rules={{ required: 'Este campo es requerido' }}
                  type="number"
                />
                <FormSelect
                  name={`parts.${index}.unidad`}
                  label="Unidad"
                  control={control}
                  rules={{ required: 'Este campo es requerido' }}
                  options={unidadOptions}
                  error={errors?.parts?.[index]?.unidad}
                />
              </Box>
            )}
          </Grid>

          {/* Intensidad de la Parte */}
          <Grid item xs={12} sm={6}>
            <FormSelect
              name={`parts.${index}.intensidad`}
              label="Intensidad"
              control={control}
              rules={{ required: 'Este campo es requerido' }}
              options={intensidadOptions}
              error={errors?.parts?.[index]?.intensidad}
            />
          </Grid>

          {/* Checkbox para Repetir Parte */}
          <Grid item xs={12} sm={6}>
            <Controller
              name={`parts.${index}.repetir`}
              control={control}
              render={({ field: controllerField }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...controllerField}
                      checked={controllerField.value || false}
                      onChange={(e) => controllerField.onChange(e.target.checked)}
                      aria-label={`Repetir Parte ${index + 1}`}
                    />
                  }
                  label="Repetir Parte"
                />
              )}
            />
          </Grid>

          {/* Repeat Count */}
          {watch(`parts.${index}.repetir`) && (
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={2} mt={2}>
                <Typography variant="body1">Repeticiones:</Typography>
                <IconButton
                  onClick={() =>
                    setValue(
                      `parts.${index}.repeatCount`,
                      Math.max((watch(`parts.${index}.repeatCount`) || 1) - 1, 1)
                    )
                  }
                  size="small"
                  aria-label="Reducir repeticiones"
                >
                  <RemoveCircleIcon />
                </IconButton>
                <Typography variant="body1">
                  {watch(`parts.${index}.repeatCount`) || 1}
                </Typography>
                <IconButton
                  onClick={() =>
                    setValue(
                      `parts.${index}.repeatCount`,
                      (watch(`parts.${index}.repeatCount`) || 1) + 1
                    )
                  }
                  size="small"
                  aria-label="Incrementar repeticiones"
                >
                  <AddCircleIcon />
                </IconButton>
              </Box>
            </Grid>
          )}

          {/* Botón para eliminar la Parte */}
          <Grid item xs={12}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => remove(index)}
              size="small"
              sx={{ mt: 1 }}
              aria-label={`Eliminar Parte ${index + 1}`}
            >
              Eliminar Parte
            </Button>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
});

function TrainingForm({
  onAddTraining,
  onEditTraining,
  trainingToEdit,
  setTrainingToEdit,
  handleCancelEdit,
  onDragEnd, // Añadir esta prop
}) {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      type: '',
      titulo: '',
      tipo: '',
      duración: '',
      intensidad: '',
      distancia: '',
      unidad: '',
      parts: [],
      detalles_adicionales: '',
      // Otros campos según el tipo de entrenamiento
    },
  });

  // Asignar un droppableId único usando useRef
  const droppableIdRef = useRef(`parts-droppable-${uuidv4()}`);

  // Determina si estamos en modo de edición
  const isEditing = trainingToEdit && trainingToEdit.id;

  // Manejo de partes dinámicas del entrenamiento
  const { fields, append, remove, insert, move } = useFieldArray({
    control,
    name: 'parts',
  });

  // Observa el tipo de entrenamiento seleccionado
  const selectedType = watch('type');

  // Observa las partes para calcular los acumulados
  const parts = watch('parts');

  // Calcular acumulado de tiempo y distancia utilizando useMemo para optimizar
  const [acumTime, acumDistance] = useMemo(() => {
    let totalTime = 0; // En segundos
    let totalDistance = 0; // En km

    parts.forEach((part) => {
      const repeat = part.repetir ? (part.repeatCount || 1) : 1;

      if (part.duración === 'Tiempo' && part.tiempo) {
        const timeParts = part.tiempo.split(':').map(Number);
        let seconds = 0;
        if (timeParts.length === 3) {
          seconds = timeParts[0] * 3600 + timeParts[1] * 60 + timeParts[2];
        } else if (timeParts.length === 2) {
          seconds = timeParts[0] * 60 + timeParts[1];
        } else if (timeParts.length === 1) {
          seconds = timeParts[0];
        }
        totalTime += seconds * repeat;
      }

      if (part.duración === 'Distancia' && part.distancia) {
        let distance = parseFloat(part.distancia);
        if (part.unidad === 'km') {
          // Ya está en km
        } else if (part.unidad === 'm') {
          distance = distance / 1000; // Convertir a km
        }
        totalDistance += distance * repeat;
      }
    });

    return [totalTime, totalDistance];
  }, [parts]);

  // Función para agregar una nueva parte
  const handleAddPart = () => {
    append({
      id: uuidv4(), // Genera un ID único usando UUID
      tipo: '',
      titulo: '',
      duración: '',
      intensidad: '',
      distancia: '',
      unidad: '',
      tiempo: '', // Campo de tiempo añadido
      repetir: false,
      repeatCount: 1,
    });
  };

  // Función para manejar el envío del formulario
  const onSubmit = (data) => {
    if (isEditing) {
      onEditTraining({ ...data, id: trainingToEdit.id });
    } else {
      onAddTraining({ ...data, id: uuidv4() }); // Genera un ID único usando UUID
    }
    // Reiniciar y cerrar el formulario
    setTrainingToEdit(null);
    handleCancelEdit();
    reset();
  };

  // Función para manejar las acciones del menú
  const handleMenuActionTraining = (action, index) => {
    switch (action) {
      case 'add':
        handleAddPart();
        break;
      case 'duplicate':
        const partToDuplicate = fields[index];
        insert(index + 1, { ...partToDuplicate, id: uuidv4() });
        break;
      case 'repeat':
        setValue(`parts.${index}.repetir`, !parts[index].repetir);
        if (!parts[index].repetir) {
          setValue(`parts.${index}.repeatCount`, 1);
        }
        break;
      case 'delete':
        remove(index);
        break;
      default:
        break;
    }
  };

  // Función para renderizar campos dinámicos según el tipo de entrenamiento
  const renderFields = () => {
    if (!selectedType) return null;

    const fieldsConfig = trainingOptions[selectedType];
    if (!fieldsConfig) return null;

    return fieldsConfig.fields.map((field) => {
      if (selectedType === 'Carrera' && field === 'tipo') {
        return (
          <Grid item xs={12} sm={6} key={field}>
            <FormSelect
              name="tipo"
              label="Tipo"
              control={control}
              rules={{ required: selectedType !== 'Descanso' ? 'Este campo es requerido' : false }}
              options={fieldsConfig.subOptions?.tipo || []}
              error={errors.tipo}
            />
          </Grid>
        );
      }

      // Campos condicionales para "duración"
      if (field === 'duración') {
        return (
          <Grid item xs={12} sm={6} key={field}>
            <FormSelect
              name="duración"
              label="Duración"
              control={control}
              rules={{ required: 'Este campo es requerido' }}
              options={duraciónOptions}
              error={errors.duración}
            />

            {watch('duración') === 'Tiempo' && (
              <FormTextField
                name="tiempo"
                label="Tiempo (hh:mm:ss)"
                control={control}
                rules={{ required: 'Este campo es requerido' }}
                placeholder="00:00:00"
                onChange={(e) => {
                  const formatted = formatTime(e.target.value);
                  setValue('tiempo', formatted);
                }}
                value={watch('tiempo') || ''}
              />
            )}

            {watch('duración') === 'Distancia' && (
              <Box display="flex" alignItems="center" gap={2} mt={2}>
                <FormTextField
                  name="distancia"
                  label="Distancia"
                  control={control}
                  rules={{ required: 'Este campo es requerido' }}
                  type="number"
                />
                <FormSelect
                  name="unidad"
                  label="Unidad"
                  control={control}
                  rules={{ required: 'Este campo es requerido' }}
                  options={unidadOptions}
                  error={errors.unidad}
                />
              </Box>
            )}
          </Grid>
        );
      }

      // Campos para "intensidad"
      if (field === 'intensidad') {
        return (
          <Grid item xs={12} sm={6} key={field}>
            <FormSelect
              name="intensidad"
              label="Intensidad"
              control={control}
              rules={{ required: 'Este campo es requerido' }}
              options={intensidadOptions}
              error={errors.intensidad}
            />
          </Grid>
        );
      }

      // Campo de título para "Carrera"
      if (selectedType === 'Carrera' && field === 'titulo') {
        return (
          <Grid item xs={12} sm={6} key={field}>
            <FormTextField
              name="titulo"
              label="Título"
              control={control}
              rules={{ required: 'Este campo es requerido' }}
            />
            {errors.titulo && (
              <Typography variant="caption" color="error">
                {errors.titulo.message}
              </Typography>
            )}
          </Grid>
        );
      }

      // Campos generales
      return (
        <Grid item xs={12} sm={6} key={field}>
          <FormTextField
            name={field}
            label={field.charAt(0).toUpperCase() + field.slice(1)}
            control={control}
            rules={{ required: 'Este campo es requerido' }}
          />
          {errors[field] && (
            <Typography variant="caption" color="error">
              {errors[field].message}
            </Typography>
          )}
        </Grid>
      );
    });
  };

  // Función para renderizar las partes del entrenamiento
      <Droppable droppableId={droppableIdRef.current}>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {fields.map((field, index) => (
              <Draggable
                key={field.id}
                draggableId={field.id}
                index={index}
              >
                {(provided, snapshot) => {
                  console.log(`Draggable ${field.id} is rendered`);
                  return (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      mb={2}
                      boxShadow={snapshot.isDragging ? 6 : 1}
                    >
                      <PartForm
                        control={control}
                        index={index}
                        field={field}
                        remove={remove}
                        handleMenuAction={handleMenuActionTraining}
                        watch={watch}
                        errors={errors}
                        dragHandleProps={provided.dragHandleProps}
                        setValue={setValue}
                      />
                    </Box>
                  );
                }}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>


  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {isEditing ? 'Editar Entrenamiento' : 'Agregar Entrenamiento'}
        </Typography>

        {/* Acumulados de la Sesión */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1">
            Tiempo Total: {secondsToHHMMSS(acumTime)}
          </Typography>
          <Typography variant="subtitle1">
            Distancia Total: {acumDistance.toFixed(2)} km
          </Typography>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            {/* Selector del tipo de entrenamiento */}
            <Grid item xs={12} sm={6}>
              <FormSelect
                name="type"
                label="Tipo de Entrenamiento"
                control={control}
                rules={{ required: 'Este campo es requerido' }}
                options={Object.keys(trainingOptions)}
                error={errors.type}
              />
            </Grid>

            {/* Campos dinámicos según el tipo de entrenamiento */}
            {renderFields()}

            {/* Partes del entrenamiento */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Partes del Entrenamiento
              </Typography>
              <DragDropContext
                onDragEnd={(result) => {
                  const { destination, source } = result;
                  console.log("Drag result:", result);

                  if (!destination || destination.index === source.index) {
                    return;
                  }

                  move(source.index, destination.index);
                }}
              >
                <Droppable droppableId={droppableIdRef.current}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {fields.map((field, index) => (
                        <Draggable
                          key={field.id}
                          draggableId={field.id}
                          index={index}
                        >
                          {(provided, snapshot) => {
                            console.log(`Draggable ${field.id} is rendered`);
                            return (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                mb={2}
                                boxShadow={snapshot.isDragging ? 6 : 1}
                              >
                                <PartForm
                                  control={control}
                                  index={index}
                                  field={field}
                                  remove={remove}
                                  handleMenuAction={handleMenuActionTraining}
                                  watch={watch}
                                  errors={errors}
                                  dragHandleProps={provided.dragHandleProps}
                                  setValue={setValue}
                                />
                              </Box>
                            );
                          }}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddPart}
                variant="outlined"
                fullWidth
                size="small"
                sx={{ mt: 2 }}
                aria-label="Añadir nueva parte"
              >
                Añadir Parte
              </Button>
            </Grid>

            {/* Otros campos según el tipo de entrenamiento */}
            {selectedType === 'Otro' && (
              <Grid item xs={12}>
                <FormTextField
                  name="detalles_adicionales"
                  label="Detalles Adicionales"
                  control={control}
                  rules={{ required: 'Este campo es requerido' }}
                  multiline
                  rows={3}
                />
                {errors.detalles_adicionales && (
                  <Typography variant="caption" color="error">
                    {errors.detalles_adicionales.message}
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>

          {/* Botones de acción */}
          <Grid container justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
            {isEditing && (
              <Grid item>
                <Button onClick={handleCancelEdit} variant="outlined" size="small">
                  Cancelar
                </Button>
              </Grid>
            )}
            <Grid item>
              <Button type="submit" variant="contained" color="primary" size="small">
                {isEditing ? 'Guardar' : 'Agregar'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
}

export default TrainingForm;
